import { Building2, FileDown, Gift, ImagePlus, Pause, Play, Plus, Trash2, X } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";

import { ApiError, apiBase } from "@/api/client";
import {
  useCreateOrganization,
  useDeleteOrganization,
  useGenerateOrgInvoice,
  useOrganizations,
  useSetOrgComped,
  useUpdateOrganization,
  useUploadOrgLogo,
} from "@/api/hooks";
import type { BillingPlan, OrganizationStatus, OrganizationSummary } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatCents, formatTime, shortId } from "@/lib/format";

export function OrganizationsPage() {
  const orgsQuery = useOrganizations();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Organizations</h1>
          <p className="mt-1 text-sm text-graphite-soft">
            Each contractor company is one organization. Users + jobs scope to the org they belong
            to. PencilOut staff (you) read across all of them.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> New organization
          </button>
        )}
      </div>

      {creating && <CreateOrgForm onClose={() => setCreating(false)} />}

      {orgsQuery.isLoading && <p className="text-sm text-graphite-soft">Loading organizations…</p>}

      {/* Hard error only when there's nothing cached to fall back to. */}
      {orgsQuery.error && !orgsQuery.data && (
        <p className="text-sm text-red-pencil">
          {orgsQuery.error instanceof ApiError
            ? orgsQuery.error.detail
            : "Could not load organizations."}
        </p>
      )}

      {orgsQuery.data && orgsQuery.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Building2 className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">
            No organizations yet. Create one to onboard a contractor company.
          </p>
        </div>
      )}

      {orgsQuery.data && orgsQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
          <table className="w-full">
            <thead className="border-b border-rule bg-paper">
              <tr>
                <th className="label-mono px-4 py-2 text-left">Name</th>
                <th className="label-mono px-4 py-2 text-left">Slug</th>
                <th className="label-mono px-4 py-2 text-left">Status</th>
                <th className="label-mono px-4 py-2 text-right">Users</th>
                <th className="label-mono px-4 py-2 text-right">Jobs</th>
                <th
                  className="label-mono px-4 py-2 text-right"
                  title="PencilOut's markup over Gemini pass-through cost. 0% = pass-through only."
                >
                  AI markup
                </th>
                <th
                  className="label-mono px-4 py-2"
                  title="comped = no billing; ai_usage_only = monthly AI bill; platform_plus_usage = subscription + AI"
                >
                  Billing plan
                </th>
                <th className="label-mono px-4 py-2 text-right">Created</th>
                <th className="label-mono px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgsQuery.data.map((org) => (
                <OrgRow key={org.id} org={org} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Inline markup editor — click to edit, type %, Enter to save.
 *  Lets the admin tweak the org's AI cost markup without leaving the
 *  list. Stores as a fraction (0.20 = 20%) but displays as percent. */
function MarkupEditor(props: {
  orgId: string;
  value: number;
  updating: boolean;
  onSave: (pct: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState((props.value * 100).toFixed(1));

  function commit() {
    const parsed = Number(draft);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 1000) {
      setDraft((props.value * 100).toFixed(1));
      setEditing(false);
      return;
    }
    const newPct = parsed / 100;
    if (Math.abs(newPct - props.value) > 1e-6) {
      props.onSave(newPct);
    }
    setEditing(false);
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft((props.value * 100).toFixed(1));
          setEditing(true);
        }}
        disabled={props.updating}
        className="rounded px-2 py-1 font-mono text-sm tabular-nums text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
        title="Click to edit"
      >
        {props.value > 0 ? `${(props.value * 100).toFixed(1)}%` : "—"}
      </button>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <input
        type="number"
        step="0.5"
        min="0"
        max="1000"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-20 rounded border border-rule bg-paper px-2 py-1 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
      />
      <span className="font-mono text-sm text-graphite-soft">%</span>
      <button
        type="button"
        onClick={commit}
        className="rounded px-2 py-1 font-mono text-xs text-graphite-soft hover:text-graphite"
      >
        save
      </button>
    </div>
  );
}

function BillingPlanEditor(props: {
  value: BillingPlan;
  updating: boolean;
  onSave: (plan: BillingPlan) => void;
}) {
  return (
    <select
      value={props.value}
      onChange={(e) => props.onSave(e.target.value as BillingPlan)}
      disabled={props.updating}
      className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs"
    >
      <option value="comped">comped</option>
      <option value="ai_usage_only">ai_usage_only</option>
      <option value="platform_plus_usage">platform_plus_usage</option>
    </select>
  );
}

function LogoButton({ org }: { org: OrganizationSummary }) {
  const upload = useUploadOrgLogo(org.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [bustKey, setBustKey] = useState(0);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    upload.mutate(f, {
      onSuccess: () => setBustKey((k) => k + 1),
    });
    e.target.value = "";
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        title={org.has_logo ? "Replace logo" : "Upload logo"}
        className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
      >
        {org.has_logo ? (
          <img
            src={`${apiBase}/organizations/${org.id}/logo?v=${bustKey}`}
            alt={`${org.name} logo`}
            className="h-4 w-4 rounded object-contain"
          />
        ) : (
          <ImagePlus className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden"
        onChange={onFile}
      />
    </>
  );
}

function InvoiceButton({ org }: { org: OrganizationSummary }) {
  const generate = useGenerateOrgInvoice(org.id);
  const [lastResult, setLastResult] = useState<{
    period: string;
    cents: number;
    invoiceId: string;
  } | null>(null);

  if (org.billing_plan === "comped") {
    return (
      <span
        className="px-1.5 font-mono text-[10px] text-graphite-faint"
        title="Comped orgs have no Stripe billing"
      >
        —
      </span>
    );
  }

  function onClick() {
    generate.mutate(
      {},
      {
        onSuccess: (resp) =>
          setLastResult({
            period: resp.period_year_month,
            cents: resp.amount_cents,
            invoiceId: resp.stripe_invoice_id,
          }),
      },
    );
  }

  const errorDetail = generate.error instanceof ApiError ? generate.error.detail : null;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={generate.isPending}
        title="Generate prior-month invoice"
        className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
      >
        <FileDown className="h-3.5 w-3.5" aria-hidden />
      </button>
      {(lastResult || errorDetail) && (
        <button
          type="button"
          aria-label="Dismiss invoice generation result"
          className="absolute right-12 mt-8 max-w-xs rounded border border-rule bg-paper p-2 text-left font-mono text-[10px] text-graphite-soft shadow"
          onClick={() => {
            generate.reset();
            setLastResult(null);
          }}
        >
          {lastResult && (
            <span>
              ✓ {lastResult.period} · {formatCents(lastResult.cents)} ·{" "}
              <span className="font-mono">{lastResult.invoiceId.slice(0, 14)}…</span>
            </span>
          )}
          {errorDetail && <span className="text-red-pencil">{errorDetail}</span>}
        </button>
      )}
    </>
  );
}

function OrgRow({ org }: { org: OrganizationSummary }) {
  const updateOrg = useUpdateOrganization(org.id);
  const deleteOrg = useDeleteOrganization();
  const setComped = useSetOrgComped(org.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingComp, setConfirmingComp] = useState(false);
  const [compReason, setCompReason] = useState("");

  // Suspend ↔ active toggle. Trial orgs flip to suspended (not back to
  // trial — once a customer has been active they shouldn't regress).
  const nextActiveStatus: OrganizationStatus = org.status === "suspended" ? "active" : "suspended";
  const isComped = org.plan_tier === "comped";
  const isPending = updateOrg.isPending || deleteOrg.isPending || setComped.isPending;

  function onToggleStatus() {
    updateOrg.mutate({ status: nextActiveStatus });
  }

  function onConfirmDelete() {
    deleteOrg.mutate(org.id, {
      onSettled: () => setConfirmingDelete(false),
    });
  }

  function onConfirmComp() {
    setComped.mutate(
      {
        comped: !isComped,
        reason: compReason.trim() || undefined,
      },
      {
        onSettled: () => {
          setConfirmingComp(false);
          setCompReason("");
        },
      },
    );
  }

  return (
    <tr className="border-b border-rule last:border-0 hover:bg-pencil-yellow-soft">
      <td className="px-4 py-3 font-medium text-graphite">{org.name}</td>
      <td className="px-4 py-3 font-mono text-xs text-graphite-soft">{org.slug}</td>
      <td className="px-4 py-3">
        <span
          className={[
            "rounded px-2 py-0.5 font-mono text-xs",
            org.status === "active"
              ? "bg-pencil-yellow-soft text-graphite"
              : org.status === "trial"
                ? "bg-paper text-graphite-soft"
                : "bg-red-pencil/10 text-red-pencil",
          ].join(" ")}
        >
          {org.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{org.user_count}</td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{org.job_count}</td>
      <td className="px-4 py-3 text-right">
        <MarkupEditor
          orgId={org.id}
          value={org.ai_markup_pct}
          updating={updateOrg.isPending}
          onSave={(pct) => updateOrg.mutate({ ai_markup_pct: pct })}
        />
      </td>
      <td className="px-4 py-3">
        <BillingPlanEditor
          value={org.billing_plan}
          updating={updateOrg.isPending}
          onSave={(plan) => updateOrg.mutate({ billing_plan: plan })}
        />
      </td>
      <td className="px-4 py-3 text-right font-mono text-xs text-graphite-soft tabular-nums">
        {formatTime(org.created_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <LogoButton org={org} />
          <InvoiceButton org={org} />
          {/* Comp toggle — Veridon-internal pilot tier. Visible only
              for staff (this whole page is staff-only anyway). When
              the org is already comped, the button shows the gift
              icon filled-yellow to signal "currently comped"; tap
              flips back to free. */}
          <button
            type="button"
            onClick={() => {
              setCompReason("");
              setConfirmingComp(true);
            }}
            disabled={isPending}
            title={isComped ? "Un-comp (revert to Free)" : "Comp this org (pilot tier)"}
            className={[
              "rounded p-1.5 transition disabled:opacity-50",
              isComped
                ? "bg-pencil-yellow-soft text-graphite hover:bg-pencil-yellow"
                : "text-graphite-soft hover:bg-paper hover:text-graphite",
            ].join(" ")}
          >
            <Gift className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onToggleStatus}
            disabled={isPending}
            title={nextActiveStatus === "active" ? "Reactivate" : "Suspend"}
            className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
          >
            {nextActiveStatus === "active" ? (
              <Play className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Pause className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            disabled={isPending}
            title="Delete organization"
            className="rounded p-1.5 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        <span className="mt-1 block font-mono text-[10px] text-graphite-faint">
          {shortId(org.id)}
          {isComped && (
            <span className="ml-1 rounded bg-pencil-yellow-soft px-1 text-graphite">comped</span>
          )}
        </span>

        {confirmingDelete && (
          <ConfirmDialog
            title={`Delete ${org.name}?`}
            body={
              <>
                This will also delete{" "}
                <span className="font-mono text-graphite">
                  {org.user_count} user{org.user_count === 1 ? "" : "s"}
                </span>{" "}
                and{" "}
                <span className="font-mono text-graphite">
                  {org.job_count} job{org.job_count === 1 ? "" : "s"}
                </span>{" "}
                belonging to this organization, plus their walkthroughs and quotes. This is{" "}
                <span className="font-semibold text-graphite">irreversible</span>.
                {deleteOrg.error && (
                  <span className="mt-2 block text-red-pencil">
                    {deleteOrg.error instanceof ApiError
                      ? deleteOrg.error.detail
                      : "Could not delete."}
                  </span>
                )}
              </>
            }
            confirmLabel="Delete organization"
            variant="danger"
            busy={deleteOrg.isPending}
            onConfirm={onConfirmDelete}
            onCancel={() => {
              setConfirmingDelete(false);
              deleteOrg.reset();
            }}
          />
        )}

        {confirmingComp && (
          <ConfirmDialog
            title={isComped ? `Un-comp ${org.name}?` : `Comp ${org.name}?`}
            body={
              <div className="text-left space-y-3">
                {isComped ? (
                  <p>
                    Reverts this org to the <strong>Free</strong> tier. They'll immediately see the
                    standard quota meter + upgrade options again. (No Stripe action — they'll need
                    to run through Checkout if they want a paid tier.)
                  </p>
                ) : (
                  <>
                    <p>
                      Flips this org to the <strong>Comped</strong> pilot tier. They'll bypass quote
                      quota entirely, see no upgrade pressure, and never get charged.
                    </p>
                    <p className="text-graphite-soft">
                      Use for Veridon-internal pilots only (e.g. dad pilot). Audit-logged either
                      way.
                    </p>
                  </>
                )}
                <label className="block">
                  <span className="label-mono">
                    Reason {isComped ? "(optional)" : "(recommended)"}
                  </span>
                  <input
                    type="text"
                    value={compReason}
                    onChange={(e) => setCompReason(e.target.value)}
                    placeholder={
                      isComped ? "e.g. Pilot ended on 2026-06-28" : "e.g. Dad pilot — 60-day comped"
                    }
                    className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-xs"
                  />
                </label>
                {setComped.error && (
                  <p className="text-red-pencil text-xs">
                    {setComped.error instanceof ApiError
                      ? setComped.error.detail
                      : "Could not update."}
                  </p>
                )}
              </div>
            }
            confirmLabel={isComped ? "Un-comp" : "Comp"}
            busy={setComped.isPending}
            onConfirm={onConfirmComp}
            onCancel={() => {
              setConfirmingComp(false);
              setCompReason("");
              setComped.reset();
            }}
          />
        )}
      </td>
    </tr>
  );
}

function CreateOrgForm({ onClose }: { onClose: () => void }) {
  const create = useCreateOrganization();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminDisplayName, setAdminDisplayName] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim() || !adminEmail.trim() || !adminDisplayName.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        slug: slug.trim() || undefined,
        admin_email: adminEmail.trim(),
        admin_display_name: adminDisplayName.trim(),
      });
      onClose();
    } catch {
      /* error surfaces below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">
          New organization
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-graphite-soft">
        We'll also create the first <span className="font-mono">org_admin</span> user and email them
        a magic-link sign-in.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block">
          <span className="label-mono">Organization name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Smith Remodeling LLC"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Slug (optional)</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="auto from name"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Admin email</span>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="owner@smithremodeling.com"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Admin display name</span>
          <input
            type="text"
            value={adminDisplayName}
            onChange={(e) => setAdminDisplayName(e.target.value)}
            placeholder="John Smith"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      {create.error && (
        <p className="text-sm text-red-pencil">
          {create.error instanceof ApiError
            ? create.error.detail
            : "Could not create organization."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          {create.isPending ? "Creating…" : "Create + invite admin"}
        </button>
      </div>
    </form>
  );
}
